import { useState } from "react";
import { motion } from "motion/react";
import { Users, MessageCircle, ArrowRight, Eye, X } from "lucide-react";
import "./TableClosedModal.css";

const HERO_IMAGE = "https://file.weltas.es/ai-studio/1779694978071_2e43bc1842c739e3.webp";

export default function TableClosedModal({
  table,
  onRequestOpen,
  onViewMenu,
  onClose,
  isSubmitting = false,
  error = "",
  rejected = false,
  rejectReason = ""
}) {
  const [name, setName] = useState("");
  const [size, setSize] = useState(2);
  const [note, setNote] = useState("");

  const canSubmit = name.trim().length >= 2 && size >= 1 && size <= 20 && !isSubmitting;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onRequestOpen({
      customerName: name.trim(),
      partySize: size,
      customerNote: note.trim() || undefined
    });
  };

  return (
    <motion.div
      className="tc-closed-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        className="tc-closed-modal"
        initial={{ scale: 0.92, y: 28, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.95, y: 12, opacity: 0 }}
        transition={{ type: "spring", damping: 22, stiffness: 320 }}
        role="dialog"
        aria-labelledby="tc-closed-title"
      >
        {onClose && (
          <button className="tc-closed-x" onClick={onClose} aria-label="Cerrar">
            <X size={20} />
          </button>
        )}

        <div className="tc-closed-hero">
          <motion.img
            src={HERO_IMAGE}
            alt="Mesa cerrada"
            initial={{ filter: "brightness(0.6) saturate(0.7)", scale: 1.08 }}
            animate={{ filter: "brightness(1) saturate(1.2)", scale: 1 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            loading="eager"
          />
          <div className="tc-closed-hero-grad" />
        </div>

        <div className="tc-closed-header">
          <h2 id="tc-closed-title">Esta mesa está cerrada</h2>
          {rejected ? (
            <>
              <p className="tc-closed-rejected">
                El personal no ha podido atender tu solicitud anterior.
                {rejectReason && <span> Motivo: <em>{rejectReason}</em></span>}
              </p>
              <p>Puedes solicitar la apertura de nuevo o ver la carta sin pedir.</p>
            </>
          ) : (
            <p>Solicita su apertura al personal para empezar a pedir.</p>
          )}
          <small>Mesa M{table?.number ?? "—"}</small>
        </div>

        <form className="tc-closed-form" onSubmit={handleSubmit}>
          <label className="tc-closed-field">
            <span>Tu nombre</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
              maxLength={80}
              placeholder="Ej: María"
              autoComplete="name"
              disabled={isSubmitting}
            />
          </label>

          <label className="tc-closed-field">
            <span>Número de personas</span>
            <div className="tc-closed-counter">
              <motion.button
                type="button"
                whileTap={{ scale: 0.92 }}
                onClick={() => setSize((s) => Math.max(1, s - 1))}
                disabled={isSubmitting || size <= 1}
                aria-label="Disminuir"
              >
                −
              </motion.button>
              <span className="tc-closed-counter-value">
                <Users size={16} /> {size}
              </span>
              <motion.button
                type="button"
                whileTap={{ scale: 0.92 }}
                onClick={() => setSize((s) => Math.min(20, s + 1))}
                disabled={isSubmitting || size >= 20}
                aria-label="Aumentar"
              >
                +
              </motion.button>
            </div>
          </label>

          <label className="tc-closed-field tc-closed-optional">
            <span>
              <MessageCircle size={14} /> Nota (opcional)
            </span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={200}
              rows={2}
              placeholder="Mesa cerca de la ventana, llegamos en 5 min, etc."
              disabled={isSubmitting}
            />
          </label>

          {error && <div className="tc-closed-error">{error}</div>}

          <div className="tc-closed-actions">
            <motion.button
              type="submit"
              className="tc-closed-primary"
              whileHover={canSubmit ? { scale: 1.02 } : {}}
              whileTap={canSubmit ? { scale: 0.97 } : {}}
              disabled={!canSubmit}
            >
              {isSubmitting ? "Enviando…" : (
                <>
                  Solicitar apertura <ArrowRight size={18} />
                </>
              )}
            </motion.button>

            <motion.button
              type="button"
              className="tc-closed-secondary"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={onViewMenu}
              disabled={isSubmitting}
            >
              <Eye size={16} /> Ver carta igual
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
